import { postWithFullResponse } from '@citrite/http';
import {
	PrepopulatedCredentials,
	WorkspaceConfiguration,
} from '@citrite/workspace-ui-platform';
import $ from 'jquery';
import URI from 'urijs';
import { resumeFormsToken } from 'Login/constants';
import { isWebViewLogon, setBrowserCloseRequiredToEndSession } from './authentication';
import { registerCustomTooltipPlugin } from './ctxsUIExtensions';
import {
	AuthenticationResult,
	registerFormsAuthenticationWidget,
	registerJQueryUI,
} from './formsAuthentication';
import { registerWebviewCredentialHandler } from './webviewCredentialHandler';

const defaultOptions = {
	prepopulatedCredentials: {},
	onAuthenticationSuccess(_authenticationInfo: any) {},
	onLogonTimeout() {},
	onAuthenticationError() {},
};

interface Options {
	prepopulatedCredentials: PrepopulatedCredentials;
	onAuthenticationSuccess(authInfo: AuthenticationResult): void;
	onLogonTimeout: Function;
	onAuthenticationError(failureId?: string): void;
}

export interface NextLogonMethodDetails {
	switchToNextLoginMethod: () => void;
}

export class ExplicitAuthController {
	private static registered = false;
	private $formContainer: JQuery;
	private options: Options;
	private $spinnerContainer: JQuery;
	private timeout: number;
	private $nextLogonMethodButton: JQuery;

	public constructor(
		private $view: JQuery,
		options: Partial<Options>,
		private workspaceConfiguration: WorkspaceConfiguration,
		private showForms: () => void,
		private routedParams: string,
		private nextLogonMethodDetails: NextLogonMethodDetails
	) {
		this.ensureRegistered();
		this.$formContainer = $view.find('.form-container');
		this.options = { ...defaultOptions, ...options } as Options;
		this.$spinnerContainer = $view.find('.explicit-auth-progress');
		this.workspaceConfiguration = workspaceConfiguration;
		this.$nextLogonMethodButton = $view.find('#linkSwitchLogonMethod');
	}

	private ensureRegistered() {
		if (!ExplicitAuthController.registered) {
			registerWebviewCredentialHandler();
			registerCustomTooltipPlugin();
			registerJQueryUI();
			registerFormsAuthenticationWidget();
			ExplicitAuthController.registered = true;
		}
	}

	public authenticate = (initialUrl: string) => {
		if (!this.$formContainer.data('ctxsCtxsFormsAuthentication')) {
			this.$formContainer.ctxsFormsAuthentication({
				informationIconUrl: 'receiver/images/common/info.svg',
				prePopulatedCredentials: this.options.prepopulatedCredentials,
				allowCancel: false,
				workspaceConfiguration: this.workspaceConfiguration,
				authenticationSuccess: (_event: any, authenticationInfo: any) => {
					this.handleAuthenticationSuccess(authenticationInfo);
				},
				authenticationError: (_event: any, authResult: any) => {
					this.handleAuthenticationError(authResult);
				},
				displayForm: this._onDisplayExplicitForm,
				formProvider: (url: string, formData: any, headers: any, callback: Function) => {
					postWithFullResponse<string>(url, formData, { headers })
						.then(response => {
							this.$view.ctxsDisplayPane();
							callback(
								$.parseXML(response.data),
								response.headers['x-citrix-explicitauthprotocol']
							);
						})
						.catch(err => {
							this.handleAuthenticationError(err);
						});
				},
				showFormContainer: this.showForms,
			});
		}

		if (this.checkForResumeForms()) {
			const data = this.resumePreviousFormsConversation();
			if (data) {
				// Resume Forms
				// Use the postback URL supplied by the webview parameters (if present) or else the URL from the configuration
				const resumeUrl =
					data.postbackUrl || this.workspaceConfiguration.authManager.webviewResumeURL;
				this.$formContainer.ctxsFormsAuthentication(
					'resumeForms',
					resumeUrl,
					data.stateContext,
					data.credentialId
				);
				// Show Spinner
				this.showSpinner();
			} else {
				// Return Error
				this.handleAuthenticationError(false);
			}
		} else {
			this.startLoginTimer();
			this.$formContainer.ctxsFormsAuthentication('startAuthentication', initialUrl);
		}
	};

	private startLoginTimer() {
		const timeoutMinutes =
			Number(this.workspaceConfiguration.authManager.loginFormTimeout) || 5;
		const safeTimeoutMinutesForClockDrift =
			timeoutMinutes === 1 ? 0.5 : timeoutMinutes - 1;
		this.timeout = window.setTimeout(() => {
			this.handleLogonTimeout();
		}, safeTimeoutMinutesForClockDrift * 60 * 1000);
	}

	private checkForResumeForms() {
		return this.routedParams.includes(resumeFormsToken);
	}

	private resumePreviousFormsConversation() {
		// Break up the parameters & remove the initial bit
		const resumeFormsSplit = this.routedParams.split(resumeFormsToken);
		// Ignore anything pre-ResumeForms
		resumeFormsSplit.shift();
		if (resumeFormsSplit.length <= 0) {
			return false;
		}

		// Divide up the info into k/v pairs & extract pertinent data
		const parameters = new URI('?' + resumeFormsSplit[0]).query(true);
		let stateContext = '';
		let postbackUrl = '';
		let credentialId = '';

		// Done this way as we don't know what the credentialID key will be.
		for (const key in parameters) {
			const value = parameters[key];
			switch (key) {
				case '_hf':
					break;
				case '_cx':
					stateContext = value;
					break;
				case '_pb':
					postbackUrl = value;
					break;
				default:
					if (!value && !credentialId) {
						credentialId = key;
					}
			}
		}

		return {
			stateContext,
			postbackUrl,
			credentialId,
		};
	}

	private _onDisplayExplicitForm = () => {
		this.hideSpinner();
		this.nextLogonMethodDetails
			? this.$nextLogonMethodButton.show()
			: this.$nextLogonMethodButton.hide();

		if (this.nextLogonMethodDetails?.switchToNextLoginMethod) {
			this.$nextLogonMethodButton.on('click', () => {
				this.nextLogonMethodDetails.switchToNextLoginMethod();
			});
		}
		// This is to make sure when you load a form it is displayed from the top of the screen and not where you scrolled to on previous screen i.e. bottom.
		this.$view.scrollTop(0);
	};

	private showSpinner() {
		this.$spinnerContainer.show();
		// this.progressSpinner.start(this.$spinner);
	}

	private hideSpinner() {
		this.$spinnerContainer.hide();
		// this.progressSpinner.stop(this.$spinner);
	}

	private handleAuthenticationSuccess(authenticationInfo: any) {
		this.cleanUp();
		// For a federated web view logon the browser needs to be closed if the IDP is NOT redirected to to end the session.
		setBrowserCloseRequiredToEndSession(
			isWebViewLogon() && !authenticationInfo.webLogoffUrl
		);
		this.options.onAuthenticationSuccess(authenticationInfo);
	}

	private handleLogonTimeout = () => {
		this.cleanUp();
		this.options.onLogonTimeout();
	};

	private handleAuthenticationError(authResult: any) {
		const isTimeout = authResult && authResult.errorId === 'sessiontimeout';

		if (isTimeout) {
			this.handleLogonTimeout();
			return;
		}

		this.cleanUp();

		const errorId = authResult && authResult.errorId;
		this.options.onAuthenticationError(errorId);
	}

	private cleanUp() {
		clearTimeout(this.timeout);
		// this.$formContainer.find('input[type="password"]').ctxsObliteratePassword();
	}
}
